import React, { lazy, useState, useContext, useMemo, useEffect, useCallback } from "react";
import { Breadcrumbs, BreadcrumbItem } from "@vds/breadcrumbs";
import { Spacer, SLNotification } from "../../components/core/utility";
import { handleChangePage, switchTab } from "./utils";
import styled from "styled-components";
import { OrderShippingSummaryAccordion } from "../C4S Decommission - Replacement/OrderShippingSummaryAccordion";
import CheckIcon from "../../assets/icons/check.svg";
import { Button } from "@vds/buttons";
import { AssignMaterialVehicleGrid } from "./AssignMaterialVehicleGrid";
import { Loader } from "@vds/loaders";
import WTCommonModal from "./WTCommonModal";
import { C4SContext } from "../../context/c4sContext";
import { generate } from "shortid";
import useOutsideClick from "../../customHook/use-outside-click";
import ConfirmationModal from "./ConfirmationModal";
import ReturnLabelContactModal from "./ReturnLabelContactModal";
import { partialReturnReship } from "../../services/C4S Decommission - Replacement/partialReurnReship";
import moment from "moment";
import { getReturnCode } from "../../../constants/ProcessingCodeConstants";
import { messageBus } from "../../../utility/messageBus";
import ReturnReshipSummaryComponent from "./ReturnReshipSummaryComponent";
import useMaterialAggregation from "./hooks/useMaterialAggregation";
import useSelections from "./hooks/useSelections";
import useVehicleData from "./hooks/useVehicleData";
import { useReturnPayload, useSummaryGrouping } from "./hooks/useReturnPayloadBuilder";

const BundleDropdownWrapper = styled.div`
  position: relative;
  display: inline-block;
  width: 680px; /* fixed width instead of min-width */
`;

const BundleDropdownTrigger = styled.button`
  width: 100%;
  text-align: left;
  padding: 0.5rem 0.75rem;
  border: 1px solid #888;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.85rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BundleOptionsContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 300px;
  overflow: auto;
  border: 1px solid #ccc;
  background: #fff;
  z-index: 20;
  border-radius: 4px;
  padding: 0.5rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
`;

const BundleOptionRow = styled.label`
  display: flex;
  align-items: center;
  justify-content: flex-start; /* left align contents */
  gap: 0.5rem;
  font-family: Verizon-NHG-eDS;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.5px;
  margin-left: 10px; /* slight left indent */
  padding: 0.25rem 0.25rem; /* uniform padding */
  text-align: left;
  cursor: pointer;
  &:hover {
    background: #f0f8ff;
  }
`;

const SelectedMaterialLabel = styled.span`
  font-family: Verizon-NHG-eDS;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.5px;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`;

const AssignMaterialComponent = (props) => {
  const sampleBundles = props.selectedMaterials;
  const { aggregatedMaterials, materialsByKey, firstKey } = useMaterialAggregation(sampleBundles);

  const [selectedHardwareKey, setSelectedHardwareKey] = useState(firstKey);
  const [isBundleDropdownOpen, setIsBundleDropdownOpen] = useState(false);

  // Initialize selections first so savedMaterialKeys is available for useVehicleData
  const {
    materialSelections,
    setMaterialSelections,
    savedMaterialKeys,
    setSavedMaterialKeys,
    selectedVehicleEntities,
    setSelectedVehicleEntities,
    isSaveEnabled: isSaveEnabledFn,
    overSelectedInfo: overSelectedInfoFn,
    isContinueEnabled, // boolean from hook
  } = useSelections();

  // Now initialize vehicle data hook with current materialsByKey and savedMaterialKeys
  const {
    vehicleList,
    isVehicleLoading,
    activeBundleId,
    combinedVehiclesByBundle,
    selectedMaterialDetails,
    materialVehiclesCache,
    setMaterialVehiclesCache,
    setVehicleList,
    setCombinedVehiclesByBundle,
    setActiveBundleId,
    setSelectedMaterialDetails,
    fetchForHardware,
    setIsVehicleLoading,
  } = useVehicleData(materialsByKey, savedMaterialKeys);

  const { buildReturnPayload } = useReturnPayload();
  const { buildSummaryGrouping } = useSummaryGrouping();
  const [returnPayload, setReturnPayload] = useState(null);
  const [actionName, setActionName] = useState("");
  const [isConfirmationModal, setIsConfirmationModal] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [summaryBundleMap, setSummaryBundleMap] = useState({});

  const currentMaterialKey = selectedHardwareKey;

  const { notification } = useContext(C4SContext);

  const toggleBundleDropdown = () => setIsBundleDropdownOpen((o) => !o);

  // Memoize the base vehicles prop and the expensive filtered computation
  const vehiclesPropBase = useMemo(
    () => (Object.keys(combinedVehiclesByBundle).length ? combinedVehiclesByBundle : vehicleList),
    [combinedVehiclesByBundle, vehicleList]
  );

  const filteredVehiclesProp = useMemo(() => {
    const selectionMeta = materialSelections[currentMaterialKey];
    const isSaved = savedMaterialKeys[currentMaterialKey];
    let result = vehiclesPropBase;
    if (isSaved && selectionMeta?.vehicleIds?.length) {
      const selectedSet = new Set(selectionMeta.vehicleIds);
      if (result && typeof result === "object" && !Array.isArray(result)) {
        const bundleFiltered = {};
        Object.keys(result).forEach((bid) => {
          const groups = result[bid] || [];
          if (Array.isArray(groups) && groups.some((g) => g && Array.isArray(g.vehicles))) {
            const newGroups = groups
              .map((grp) => ({
                ...grp,
                vehicles: (grp.vehicles || []).filter((v) => selectedSet.has(v.id || v.vehicle)),
              }))
              .filter((grp) => grp.vehicles.length);
            bundleFiltered[bid] = newGroups;
          } else {
            bundleFiltered[bid] = (groups || []).filter((v) => selectedSet.has(v.id || v.vehicle));
          }
        });
        result = bundleFiltered;
      } else if (Array.isArray(result)) {
        result = result.filter((v) => selectedSet.has(v.id || v.vehicle));
      }
    }
    return result;
  }, [vehiclesPropBase, materialSelections, currentMaterialKey, savedMaterialKeys]);

  // Precompute fast lookup indices to avoid repeated filtering inside selection handler
  const bundleMaterialIndex = useMemo(() => {
    const idx = {};
    (sampleBundles || []).forEach((rec) => {
      const bundleId = rec?.bundleId || "";
      const key = `${bundleId}|${rec?.hardwareId}|${rec?.subscriptionId}`;
      if (!idx[key]) idx[key] = { defaultWorkTicket: rec?.workTicketId || "", esnToWT: {} };
      if (!idx[key].defaultWorkTicket && rec?.workTicketId) idx[key].defaultWorkTicket = rec.workTicketId;
      if (rec?.esn) idx[key].esnToWT[rec.esn] = rec?.workTicketId || idx[key].defaultWorkTicket || "";
    });
    return idx;
  }, [sampleBundles]);

  const materialIndex = useMemo(() => {
    const idx = {};
    (sampleBundles || []).forEach((rec) => {
      const key = `${rec?.hardwareId}|${rec?.subscriptionId}`;
      const prev = idx[key] || { defaultWorkTicket: "", esnToWT: {}, firstEsn: "", count: 0 };
      const defaultWorkTicket = prev.defaultWorkTicket || rec?.workTicketId || "";
      const firstEsn = prev.firstEsn || rec?.esn || "";
      const esnToWT = { ...prev.esnToWT };
      if (rec?.esn) esnToWT[rec.esn] = rec?.workTicketId || defaultWorkTicket || "";
      idx[key] = { defaultWorkTicket, esnToWT, firstEsn, count: prev.count + 1 };
    });
    return idx;
  }, [sampleBundles]);

  const materialAddrContactIndex = useMemo(() => {
    const idx = {};
    (sampleBundles || []).forEach((rec) => {
      const key = `${rec?.hardwareId}|${rec?.subscriptionId}`;
      const prev = idx[key] || {};
      const addressId = prev.addressId || rec?.shippingAddress?.addressId || "";
      const contactId = prev.contactId || rec?.contact?.contactId || "";
      idx[key] = { addressId, contactId };
    });
    return idx;
  }, [sampleBundles]);

  const handleClickOutside = () => {
    notification.setNotification(null);
  };
  const clickRef = useOutsideClick(handleClickOutside);
  const notify = useCallback(
    (content, type) => {
      notification.setNotification(<SLNotification key={generate()} type={type} title={content} fullBleed={true} ref={clickRef} />);
    },
    [notification, clickRef]
  );

  const handleHardwareSelect = useCallback(
    async (key) => {
      setSelectedHardwareKey(key);
      setIsBundleDropdownOpen(false);
      await fetchForHardware({ key, notify });
    },
    [fetchForHardware, notify]
  );

  useEffect(() => {
    setMaterialSelections((prev) => {
      const next = { ...prev };
      aggregatedMaterials.forEach((item) => {
        const key = `${item.hardwareId}|${item.subscriptionId}`;
        const related = sampleBundles.filter((rec) => rec.hardwareId === item.hardwareId && rec.subscriptionId === item.subscriptionId);
        const addressIds = related.map((r) => r.address?.addressId).filter(Boolean);
        const contactIds = related.map((r) => r.contact?.contactId).filter(Boolean);
        const workticketIds = related.map((r) => r.workTicketId).filter(Boolean);
        const esns = related.map((r) => r.esn).filter(Boolean);
        if (!next[key]) {
          next[key] = {
            requestedQuantity: item.requestedQuantity,
            vehicleIds: [],
            hardwareId: item.hardwareId,
            subscriptionId: item.subscriptionId,
            workticketId: workticketIds[0] || "",
            esn: esns[0] || "",
            addressId: addressIds[0] || "",
            contactId: contactIds[0] || "",
          };
        } else {
          next[key] = {
            ...next[key],
            requestedQuantity: item.requestedQuantity,
            hardwareId: item.hardwareId,
            subscriptionId: item.subscriptionId,
            workticketId: next[key].workticketId || workticketIds[0] || "",
            esn: next[key].esn || esns[0] || "",
            addressId: next[key].addressId || addressIds[0] || "",
            contactId: next[key].contactId || contactIds[0] || "",
          };
        }
      });
      return next;
    });
  }, [aggregatedMaterials, sampleBundles, setMaterialSelections]);

  const computeTempVehicleKey = (v, hardwareId, subscriptionId) => {
    const base = v?.vin || v?.vehicle || v?.id || v?.serial || `${v?.year || ""}-${v?.make || ""}-${v?.model || ""}`;
    return `${base}-${hardwareId || ""}-${subscriptionId || ""}`;
  };

  const handleVehicleSelectionChange = ({ materialKey, vehicleIds }) => {
    const [hardwareId, subscriptionId] = materialKey.split("|");
    const defaults = materialIndex[materialKey] || { defaultWorkTicket: "", firstEsn: "" };
    const addrContact = materialAddrContactIndex[materialKey] || { addressId: "", contactId: "" };

    setMaterialSelections((prev) => ({
      ...prev,
      [materialKey]: {
        ...(prev[materialKey] || { requestedQuantity: materialIndex[materialKey]?.count || 0, vehicleIds: [] }),
        requestedQuantity: prev[materialKey]?.requestedQuantity || materialIndex[materialKey]?.count || 0,
        vehicleIds,
        hardwareId,
        subscriptionId,
        workticketId: prev[materialKey]?.workticketId || defaults.defaultWorkTicket || "",
        esn: prev[materialKey]?.esn || defaults.firstEsn || "",
        addressId: prev[materialKey]?.addressId || addrContact.addressId,
        contactId: prev[materialKey]?.contactId || addrContact.contactId,
      },
    }));

    const allVehicles = [];
    if (combinedVehiclesByBundle && typeof combinedVehiclesByBundle === "object" && !Array.isArray(combinedVehiclesByBundle)) {
      Object.entries(combinedVehiclesByBundle).forEach(([bundleId, bundleGroups]) => {
        const key = `${bundleId}|${hardwareId}|${subscriptionId}`;
        const bundleMeta = bundleMaterialIndex[key] || { defaultWorkTicket: "", esnToWT: {} };
        if (Array.isArray(bundleGroups)) {
          bundleGroups.forEach((g) => {
            if (g && Array.isArray(g.vehicles)) {
              g.vehicles.forEach((veh) => {
                const derivedWT = veh?.esn ? bundleMeta.esnToWT[veh.esn] || bundleMeta.defaultWorkTicket : bundleMeta.defaultWorkTicket;
                const enriched = {
                  ...veh,
                  bundleId,
                  hardwareId,
                  subscriptionId,
                  workticketId: derivedWT || "",
                  _stableId: computeTempVehicleKey(veh, hardwareId, subscriptionId),
                  addressId: g.address?.addressId || addrContact.addressId,
                  contactId: g.contact?.contactId || addrContact.contactId,
                };
                allVehicles.push(enriched);
              });
            }
          });
        }
      });
    } else if (Array.isArray(vehicleList)) {
      const matMeta = materialIndex[materialKey] || { defaultWorkTicket: "", esnToWT: {} };
      vehicleList.forEach((v) => {
        const derivedWT = v?.esn ? matMeta.esnToWT[v.esn] || matMeta.defaultWorkTicket : matMeta.defaultWorkTicket;
        const enriched = {
          ...v,
          hardwareId,
          subscriptionId,
          _stableId: computeTempVehicleKey(v, hardwareId, subscriptionId),
          addressId: addrContact.addressId,
          contactId: addrContact.contactId,
          workticketId: derivedWT || "",
        };
        allVehicles.push(enriched);
      });
    }

    const vehicleIdSet = new Set(vehicleIds);
    const matched = allVehicles.filter((v) => vehicleIdSet.has(v._stableId) || vehicleIdSet.has(v.id) || vehicleIdSet.has(v.vehicle));
    setSelectedVehicleEntities((prev) => ({ ...prev, [materialKey]: matched }));
  };

  const isSaveEnabled = isSaveEnabledFn(currentMaterialKey);
  const overSelectedInfo = overSelectedInfoFn(currentMaterialKey);

  const [commonModalStatus, setCommonModalStatus] = useState(false);
  const [title, setTitle] = useState("");
  const [subTitle, setSubTitle] = useState("");
  const [actionItem, setActionItem] = useState("");
  const [showReturnLabelModal, setShowReturnLabelModal] = useState(false);
  const cspDetails = useMemo(() => {
    try {
      const details = sessionStorage.getItem("CSPDetails");
      return details ? JSON.parse(details) : null;
    } catch (error) {
      // Swallow parsing errors to avoid breaking render
      return null;
    }
  }, []);

  const bundleNameMap = useMemo(() => {
    const map = {};
    const products = cspDetails?.Product || [];
    products.forEach((p) => {
      if (p?.BundleId && map[p.BundleId] === undefined) {
        map[p.BundleId] = p.BundleName || p.BundleId;
      }
    });
    return map;
  }, [cspDetails]);

  const [reason, setReason] = useState("");
  const [subReason, setSubReason] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const salesOrgId = cspDetails?.CSP?.filter((csp) => csp.DefaultCSP === "1")[0]?.Opp_Sales_Org || "";

  const handleSaveAssignment = () => {
    if (!isSaveEnabled || !currentMaterialKey) return;
    setActionName("assignment");
    setIsConfirmationModal(true);
  };

  const handleContinueAssigment = () => {
    if (!currentMaterialKey) {
      setIsConfirmationModal(false);
      return;
    }
    if (isSaveEnabled && !savedMaterialKeys[currentMaterialKey]) {
      setSavedMaterialKeys((prev) => ({ ...prev, [currentMaterialKey]: true }));
      if (!materialVehiclesCache[currentMaterialKey]) {
        materialVehiclesCache[currentMaterialKey] = combinedVehiclesByBundle;
      }
    }
    setIsConfirmationModal(false);
  };

  const handleContinueAll = () => {
    if (isContinueEnabled) {
      const built = buildReturnPayload({
        savedMaterialKeys,
        materialsByKey,
        sampleBundles,
        selectedVehicleEntities,
        getReturnCode,
      });
      setReturnPayload(built);

      // Use shared builder to create the summary view mapping instead of duplicating logic
      const summaryMap = buildSummaryGrouping({
        savedMaterialKeys,
        materialVehiclesCache,
        selectedVehicleEntities,
      });
      const hasAnySelected = Object.keys(summaryMap).some((b) => (summaryMap[b] || []).some((grp) => (grp.vehicles || []).length));
      setSummaryBundleMap(hasAnySelected ? summaryMap : combinedVehiclesByBundle);
      setShowSummary(true);
    }
    setTitle("Select Reason for Return/Reship or Return");
    setSubTitle("Select Reason");
    setActionItem(props.actionName);
  };

  const handleSummaryBack = () => {
    setShowSummary(false);
  };
  const handleSummaryContinue = (opts) => {
    const disabledKeys = Array.isArray(opts?.disabledKeys) ? new Set(opts.disabledKeys) : new Set();
    const disabledEsnObjects = Array.isArray(opts?.disabledEsnObjects) ? opts.disabledEsnObjects : [];
    if (disabledKeys.size > 0) {
      const built = buildReturnPayload({
        savedMaterialKeys,
        materialsByKey,
        sampleBundles,
        selectedVehicleEntities,
        getReturnCode,
        disabledKeys,
        disabledEsnObjects,
      });
      setReturnPayload(built);
    }

    setCommonModalStatus(true);
  };

  const handleContinue = () => {
    if (actionName === "contact") {
      setIsConfirmationModal(false);
      setShowReturnLabelModal(true);
    } else if (actionName === "create_workticket") {
      setIsConfirmationModal(false);
      handlePartialReturnReship();
    }
  };

  const handlePartialReturnReship = async () => {
    setIsVehicleLoading(true);
    try {
      const firstCsp = cspDetails?.CSP?.[0];
      const payload = {
        action: props.actionName === "return" ? "Return" : "Reship",
        createdBy: `${cspDetails?.firstName} ${cspDetails?.lastName}`,
        createdByEmail: cspDetails?.email,
        createdByEid: cspDetails?.Employee_ID,
        accountName: cspDetails?.AccountName,
        assignTo: cspDetails?.Employee_ID,
        assignedTo: firstCsp?.CSPOwner,
        assignedToEmail: firstCsp?.CSPOwnerEmail,
        assignedToEid: firstCsp?.CSPOwnerEId,
        erpProductRecipientPartyId: cspDetails?.ResellerBusinessPartnerNumber || cspDetails?.ERP_Partner_Number,
        erpBusinessPartnerId: cspDetails?.ResellerBusinessPartnerNumber || cspDetails?.ERP_Partner_Number,
        resellerBusinessPartnerNumber: cspDetails?.ResellerBusinessPartnerNumber || cspDetails?.ERP_Partner_Number || "",
        erpTicketType: firstCsp?.CSPID ? "ZINS" : "ZSRQ",
        distributionChannel: cspDetails?.distributionChannel || "02",
        division: cspDetails?.division || "02",
        requestDate: moment().format("YYYYMMDD"),
        crmReferenceId: firstCsp?.CRM_Ref_ID || "",
        documentType: firstCsp?.CSPID ? "ZINS" : "ZSRQ",
        documentDate: moment().format("YYYYMMDD"),
        salesOrg: salesOrgId ? salesOrgId : "",
        contractAccount: cspDetails?.CONTRACT_ACCOUNT_ID?.[0] || "",
        erpPrimaryContactNumber: cspDetails?.ERP_Primary_Contact_NUmber ?? "",
        cspId: firstCsp?.CSPID,
        returnLabelName: name ? name : "",
        returnLabelPhone: phone ? phone : "",
        returnLabelEmail: email ? email : "",
        returnReason: reason ? reason : "",
        returnSubReason: subReason ? subReason : "",
        returns: returnPayload.returns,
      };

      const response = await partialReturnReship(payload);
      if (response?.status === 200 && response?.data) {
        notify("Return processed successfully", "success");
        switchTab(3);
      } else {
        const errorMsg = response?.data?.message || "Unable to process return due to an unexpected error";
        notify(errorMsg, "error");
      }
    } catch (error) {
      notify("An error occurred while processing the return.", "error");
    } finally {
      setIsVehicleLoading(false);
      setTimeout(() => {
        notification.setNotification(null);
      }, 3000);
    }
  };

  const handleContactModal = (reasonDescription, value) => {
    if (value === "reason") {
      setReason(reasonDescription);
      setSubReason("");
    } else if (value === "subReason") {
      setSubReason(reasonDescription);
    }
    setActionName("contact");
    setIsConfirmationModal(true);
  };

  useEffect(() => {
    const hasAnyVehicles = Array.isArray(vehicleList)
      ? vehicleList.length > 0
      : vehicleList && typeof vehicleList === "object" && Object.keys(vehicleList).length > 0;

    if (selectedHardwareKey && !hasAnyVehicles) {
      fetchForHardware({ key: selectedHardwareKey, notify });
    }
  }, [selectedHardwareKey, vehicleList, fetchForHardware, materialsByKey, savedMaterialKeys, notify]);

  const handleCloseModal = () => {
    setShowReturnLabelModal(false);
  };

  const handleSaveData = (name, phone, email) => {
    setName(name);
    setPhone(phone);
    setEmail(email);
    setShowReturnLabelModal(false);
    setActionName("create_workticket");
    setIsConfirmationModal(true);
  };

  const handleNo = () => {
    setName("");
    setPhone("");
    setEmail("");
    setIsConfirmationModal(false);
    setActionName("create_workticket");
    setIsConfirmationModal(true);
  };

  const handleShipmentDamagedModal = (reasonDescription, value) => {
    if (value === "reason") {
      setReason(reasonDescription);
    }
    setTitle("Who Damaged/Lost the Package?");
    setSubTitle("Select Who Damaged/Lost the Package");
    setActionItem("Damaged_Lost_Package");
    setCommonModalStatus(true);
  };

  const handleBackClick = () => {
    setActionName("back");
    setIsConfirmationModal(true);
  };

  const handleBackContinue = () => {
    setMaterialSelections({});
    setSavedMaterialKeys({});
    setSelectedVehicleEntities({});
    setMaterialVehiclesCache({});
    setSelectedHardwareKey("");
    setVehicleList([]);
    setCombinedVehiclesByBundle({});
    setActiveBundleId("");
    setSelectedMaterialDetails(null);
    try {
      messageBus?.trigger && messageBus.trigger("resetFilters");
    } catch (e) {
      // no-op if messageBus unavailable
    }
    setIsConfirmationModal(false);
    handleChangePage({ index: 0 });
  };

  return (
    <div>
      {showSummary ? (
        <ReturnReshipSummaryComponent
          combinedVehiclesByBundle={summaryBundleMap}
          selectedVehicleEntities={selectedVehicleEntities}
          onBack={handleSummaryBack}
          onContinue={handleSummaryContinue}
          selectedMaterialDetails={sampleBundles}
          onRequestEsns={async (materialKey, workticketId) => {
            // materialKey is typically `${hardwareId}|${subscriptionId}`
            const [hardwareId, subscriptionId] = materialKey.split("|");
            // Find all entries in selectedMaterialDetails (sampleBundles) that match
            const matches = (sampleBundles || []).filter(
              (item) =>
                String(item.hardwareId) === String(hardwareId) &&
                String(item.subscriptionId) === String(subscriptionId) &&
                (!workticketId || String(item.workTicketId) === String(workticketId))
            );
            // Collect all ESNs from matching entries
            const esns = matches.flatMap((item) => (Array.isArray(item.esns) ? item.esns : item.esn ? [item.esn] : []));
            // Remove duplicates
            return Array.from(new Set(esns.filter(Boolean)));
          }}
        />
      ) : (
        <>
          <Spacer top="10px" />
          <Breadcrumbs surface="light">
            <BreadcrumbItem key={0} active={false}>
              <span
                css={{
                  fontWeight: "400",
                  color: "black",
                }}
                onClick={() => {
                  handleChangePage({ index: 0 });
                }}
              >
                Return / Reship
              </span>
            </BreadcrumbItem>
            <BreadcrumbItem key={1} active={false}>
              <span
                css={{
                  fontWeight: "700",
                  color: "black",
                }}
              >
                Assign Materials
              </span>
            </BreadcrumbItem>
          </Breadcrumbs>
          <Spacer bottom="1rem" />
          <OrderShippingSummaryAccordion />
          <Loader active={isVehicleLoading} fullscreen />
          <div style={{ margin: "1rem 0", width: "100%" }}>
            <div style={{ display: "flex", alignItems: "flex-end", width: "100%", gap: "1rem" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem", maxWidth: "680px", flexGrow: 0 }}>
                <label style={{ fontWeight: 600, fontSize: "12px" }}>Selected Materials:</label>
                <BundleDropdownWrapper>
                  <BundleDropdownTrigger type="button" onClick={toggleBundleDropdown} aria-haspopup="listbox" aria-expanded={isBundleDropdownOpen}>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      {selectedMaterialDetails ? (
                        <SelectedMaterialLabel>
                          <strong>{`${selectedMaterialDetails.requestedQuantity} Requested | `}</strong>
                          {(() => {
                            const base = `${selectedMaterialDetails.hardwareName} (${selectedMaterialDetails.hardwareId}) | ${selectedMaterialDetails.subscriptionName} (${selectedMaterialDetails.subscriptionId})`;
                            const MAX_LEN = 90;
                            return base.length > MAX_LEN ? base.slice(0, MAX_LEN) + "..." : base;
                          })()}
                        </SelectedMaterialLabel>
                      ) : (
                        <SelectedMaterialLabel>Choose hardware item</SelectedMaterialLabel>
                      )}
                    </span>
                    <span style={{ fontSize: "0.7rem", flexShrink: 0 }}>{isBundleDropdownOpen ? "▲" : "▼"}</span>
                  </BundleDropdownTrigger>
                  {isBundleDropdownOpen && (
                    <BundleOptionsContainer role="listbox">
                      {aggregatedMaterials.map((item) => {
                        const key = `${item.hardwareId}|${item.subscriptionId}`;
                        const isSaved = savedMaterialKeys[key];
                        return (
                          <BundleOptionRow key={key} onClick={() => handleHardwareSelect(key)}>
                            {isSaved && <img src={CheckIcon} alt="saved" height="14" width="14" style={{ display: "inline-block" }} />}
                            <span>
                              <strong>{`${item.requestedQuantity} Requested | `}</strong>
                              {(() => {
                                const base = `${item.hardwareName} (${item.hardwareId}) | ${item.subscriptionName} (${item.subscriptionId})`;
                                const MAX_LEN = 120;
                                return base.length > MAX_LEN ? base.slice(0, MAX_LEN) + "..." : base;
                              })()}
                            </span>
                          </BundleOptionRow>
                        );
                      })}
                    </BundleOptionsContainer>
                  )}
                </BundleDropdownWrapper>
              </div>
              <div style={{ display: "flex", gap: "0.5rem", marginLeft: "auto", marginTop: "22px", alignItems: "flex-start" }}>
                <div style={{ position: "relative", display: "flex", alignItems: "center", gap: "8px" }}>
                  <Button
                    size="small"
                    variant="secondary"
                    onClick={handleBackClick}
                    aria-label="Back"
                    style={{ backgroundColor: "#ffffff", color: "#000", border: "1px solid #ccc" }}
                  >
                    Back
                  </Button>
                  <Button size="small" variant="primary" disabled={!isSaveEnabled} onClick={handleSaveAssignment} aria-label="Save Assignment">
                    Save Assignment
                  </Button>
                  <Button size="small" variant="secondary" disabled={!isContinueEnabled} onClick={handleContinueAll} aria-label="Continue">
                    Continue
                  </Button>
                  {overSelectedInfo && (
                    <span
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 10,
                        marginTop: "4px",
                        color: "#c00",
                        fontSize: "11px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Selected {overSelectedInfo.selected} vehicles; requested quantity is {overSelectedInfo.requested}.
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          {selectedHardwareKey &&
            (() => {
              const [hId, sId] = selectedHardwareKey.split("|");
              const materialFilter = { hardwareId: hId, subscriptionId: sId };
              return (
                <div style={{ marginTop: "2rem" }}>
                  {(() => {
                    const selectionMeta = materialSelections[currentMaterialKey];
                    const isSaved = savedMaterialKeys[currentMaterialKey];
                    // Use memoized filteredVehiclesProp instead of recalculating each render
                    if (
                      filteredVehiclesProp === null ||
                      (typeof filteredVehiclesProp === "object" &&
                        !Array.isArray(filteredVehiclesProp) &&
                        Object.keys(filteredVehiclesProp).length === 0)
                    ) {
                      return (
                        <div style={{ padding: "1rem", fontSize: "14px", color: "#444" }}>
                          No vehicle found for this material, exclude it to continue return/reship process
                        </div>
                      );
                    }
                    return (
                      <AssignMaterialVehicleGrid
                        bundleIds={activeBundleId ? [activeBundleId] : []}
                        addressId={props?.data?.addressId || props?.navigationData?.addressId || ""}
                        contactId={props?.data?.contactId || props?.navigationData?.contactId || ""}
                        crmRefId={cspDetails?.CSP?.[0]?.CRM_Ref_ID || ""}
                        materialFilter={materialFilter}
                        vehicleList={filteredVehiclesProp}
                        activeBundleId={activeBundleId}
                        selectedMaterialDetails={selectedMaterialDetails}
                        materialKey={currentMaterialKey}
                        externalSelectedVehicleIds={selectionMeta?.vehicleIds || []}
                        onSelectionChange={handleVehicleSelectionChange}
                        selectionLocked={isSaved === true}
                        bundleNameMap={bundleNameMap}
                      />
                    );
                  })()}
                </div>
              );
            })()}
        </>
      )}
      {commonModalStatus && (
        <WTCommonModal
          commonModalStatus={commonModalStatus}
          setCommonModalStatus={setCommonModalStatus}
          title={title}
          subTitle={subTitle}
          actionItem={actionItem}
          notify={notify}
          handleContactModal={handleContactModal}
          handleShipmentDamagedModal={handleShipmentDamagedModal}
        />
      )}

      {isConfirmationModal && actionName === "assignment" && (
        <ConfirmationModal
          isConfirmationModal={isConfirmationModal}
          setIsConfirmationModal={setIsConfirmationModal}
          handleContinue={handleContinueAssigment}
          modalHeading="Confirmation"
          modalBody="You will not be able to make any changes after you save the assignment. Please make sure these are the vehicles you want assigned for the hardware. Otherwise, you will have to start the process over if you want to change a vehicle."
          actionName={actionName}
        />
      )}

      {isConfirmationModal && actionName === "contact" && (
        <ConfirmationModal
          isConfirmationModal={isConfirmationModal}
          setIsConfirmationModal={setIsConfirmationModal}
          modalHeading="Return Label Contact"
          modalBody="Do you need a return label?"
          actionName={actionName}
          handleContinue={handleContinue}
          handleNo={handleNo}
        />
      )}

      {isConfirmationModal && actionName === "create_workticket" && (
        <ConfirmationModal
          isConfirmationModal={isConfirmationModal}
          setIsConfirmationModal={setIsConfirmationModal}
          modalHeading="Return Ticket Confirmation"
          modalBody="Are you sure you want to proceed with creating a return ticket for a return or return and reship?"
          actionName={actionName}
          handleContinue={handleContinue}
        />
      )}

      {isConfirmationModal && actionName === "back" && (
        <ConfirmationModal
          isConfirmationModal={isConfirmationModal}
          setIsConfirmationModal={setIsConfirmationModal}
          handleContinue={handleBackContinue}
          modalHeading="Confirm Going Back"
          modalBody="All changes during the return/reship in the vehicle will be lost if you go back."
          actionName={actionName}
          continueButtonLabel="Continue"
        />
      )}

      {showReturnLabelModal && (
        <ReturnLabelContactModal
          setisShowReturnLabelModal={setShowReturnLabelModal}
          onClose={handleCloseModal}
          title="Return Label Contact"
          handleSaveData={handleSaveData}
        />
      )}
    </div>
  );
};

export default AssignMaterialComponent;

export const AssignMaterialComponentMetadata = {
  name: "Assign Material",
  id: "assign_material",
  component: lazy(() => import("./AssignMaterialComponent")),
  route: "/connect360/assign_material",
  Breadcrumbs: true,
};
